import { i18n } from 'next-i18next'
import { URLParams } from 'modules/url-params/state/app-url-params-types'
import { OrderContactSlice } from './contact-types'
import { ObjectSchema, boolean, object, string } from 'yup'
import { Config } from 'modules/config/state/config-types'
import { PHONE_VALIDATION_REGEX, FIELD_CHARACTER_LIMITS } from 'utils/constants/misc'

const EMAIL_VALIDATION_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const makeOrderContactInitialState = (
  config: Config,
  urlParams: URLParams,
): OrderContactSlice => ({
  firstName: urlParams.first || '',
  lastName: urlParams.last || '',
  email: urlParams.email || '',
  phone: urlParams.phone || '',
  optIn: config.optIn.default,
  optInSms: config.optInSms.default,
  isNameAnonymous: false,
  isAmountAnonymous: false,
  isCompanyCard: false,
  cardCompanyName: '',
})

export const makeOrderContactValidationSchema = (
  isPhoneRequired: boolean,
): ObjectSchema<OrderContactSlice> => {
  const t = i18n?.t.bind(i18n) || ((key: string, fallback: string) => fallback)
  const REQUIRED_MESSAGE = t('OrderContact.validation.fieldRequired', 'This field is required')

  return object({
    firstName: string()
      .required(t('OrderContact.validation.firstNameRequired', 'Enter your first name'))
      .max(
        FIELD_CHARACTER_LIMITS.FIRST_NAME,
        t('validation.truncation.firstName', 'First name must be 32 characters or less'),
      ),
    lastName: string()
      .required(t('OrderContact.validation.lastNameRequired', 'Enter your last name'))
      .max(
        FIELD_CHARACTER_LIMITS.LAST_NAME,
        t('validation.truncation.lastName', 'Last name must be 32 characters or less'),
      ),
    email: string()
      .required(t('OrderContact.validation.emailRequired', 'Enter a valid email address'))
      .max(
        FIELD_CHARACTER_LIMITS.EMAIL,
        t('validation.truncation.email', 'Email must be 320 characters or less'),
      )
      .matches(
        EMAIL_VALIDATION_REGEX,
        t('OrderOrderContact.validation.emailFormat', 'Enter a valid email address'),
      ),
    phone: string()
      .test(
        'required',
        t('ContactAnswers.validation.invalidPhone', 'Enter a valid phone number'),
        (value?: string) => (isPhoneRequired ? !!value : true),
      )
      .max(
        FIELD_CHARACTER_LIMITS.PHONE,
        t('validation.truncation.phone', 'Phone number must be 16 characters or less'),
      )
      .matches(PHONE_VALIDATION_REGEX, {
        message: t('ContactAnswers.validation.invalidPhone', 'Enter a valid phone number'),
        excludeEmptyString: true,
      }),
    optIn: boolean().required(REQUIRED_MESSAGE),
    optInSms: boolean().required(REQUIRED_MESSAGE),
    isNameAnonymous: boolean().required(REQUIRED_MESSAGE),
    isAmountAnonymous: boolean().required(REQUIRED_MESSAGE),
    isCompanyCard: boolean().default(false),
    cardCompanyName: string().when('isCompanyCard', {
      is: true,
      then: () => string().required(REQUIRED_MESSAGE),
      otherwise: () => string().notRequired(),
    }),
  })
}
